import type {
  ShortLink,
  AuthResponse,
  User,
  Event,
  Order,
  Transaction,
  Vendor,
  VendorEvent,
  VendorStats,
  VendorCountStats,
  VendorEventCountStats,
  VendorAdministrator,
  VendorEventAdministrator,
  EventAdministrator,
  PermissionGroup,
  MenuItem,
  Voucher,
  AdminPaymentDevice,
  EventTerminalDevice,
  EventPosGrants,
  PosGrant,
  OrganizationContactEvents,
  OrganizationContactsPage,
  SupportReloadsReport,
  TerminalAttempt,
  TerminalSdkSession,
  PaginatedResponse,
  Organization,
  ContentPage,
  UserTransactionMetrics,
  UserEventStats,
  RedeemableItem,
  WalletPass,
  FinancialsResponse,
  EventHomePage,
  EventNavigation,
  EventCategory,
  EventHomePageNavigation,
  NavigationLinkType,
  EventPayFunction,
  EventInsightsStats,
  MapConfig,
  HeatmapResponse,
  TimelineResponse,
  JourneyResponse,
  LiveStatsResponse,
  LiveStatsParams,
  OrderReview,
  OrderReviewSummary,
  ItemReview,
  ItemReviewSummary,
  OpsOverview,
  OpsEventTotals,
  OpsSystem,
} from './types'
import type {
  ChatSession,
  ChatSessionSummary,
  ChatResolutionStatus,
  ChatStreamEvent,
  PendingAction,
  SupportChatAnalytics,
  SupportChatAnalyticsParams,
  ToolResult,
} from './support-chat-types'

import Bugsnag from '@bugsnag/js'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.eventpay.com/en/api'
const FRONTEND_URL = import.meta.env.VITE_FRONTEND_URL || 'https://app.eventpay.com'

// Debug: log the API base URL on load
console.log('API_BASE_URL:', API_BASE_URL)

// Request deduplication: prevents duplicate in-flight requests
// Key: URL + token hash, Value: Promise
const inFlightRequests = new Map<string, Promise<unknown>>()

// Simple hash for token to use in cache key (don't store full token)
function hashToken(token: string): string {
  return token.slice(0, 16)
}

function notifyApiError(error: Error, endpoint: string, method: string, statusCode?: number) {
  if (!Bugsnag.isStarted()) return
  Bugsnag.notify(error, (event) => {
    event.addMetadata('api', { endpoint, method, statusCode, baseUrl: API_BASE_URL })
    if (statusCode === 401 || statusCode === 403) {
      event.severity = 'info'
    }
  })
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  token?: string
  params?: Record<string, string | number | boolean | undefined | null>
  headers?: Record<string, string>
  // Statuses that are an expected outcome for this call (e.g. a 403 used as a
  // yes/no membership probe). Still thrown to the caller, but not reported to
  // error tracking.
  expectedErrorStatuses?: number[]
}

type ParamsObject = Record<string, string | number | boolean | undefined | null>

/** A binary response (a PDF to preview), with the same auth and error shape as `request`. */
export async function requestBlob(endpoint: string, token: string): Promise<Blob> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, { headers: { Authorization: `Token ${token}` } })
  if (!response.ok) {
    const data = await response.json().catch(() => ({ detail: 'Request failed' }))
    throw Object.assign(new Error(`${response.status}: ${data?.detail || 'Request failed'}`), { status: response.status, data })
  }
  return response.blob()
}

export async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, token, params, expectedErrorStatuses } = options

  const url = new URL(`${API_BASE_URL}${endpoint}`)
  
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, String(value))
      }
    })
  }

  // For GET requests, deduplicate in-flight requests
  const cacheKey = method === 'GET' ? `${url.toString()}:${token ? hashToken(token) : 'anon'}` : null
  
  if (cacheKey) {
    const existingRequest = inFlightRequests.get(cacheKey)
    if (existingRequest) {
      console.log(`[API] Deduplicating request: ${endpoint}`)
      return existingRequest as Promise<T>
    }
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }

  if (token) {
    headers['Authorization'] = `Token ${token}`
  }

  const fetchPromise = fetch(url.toString(), {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  })

  // DRF errors come in several shapes: {detail: "..."}, ["..."], and
  // {field: ["..."]}. Flatten whichever we got into a readable message so
  // callers don't show a generic "Request failed" over a real explanation.
  const formatApiError = (payload: unknown): string => {
    if (typeof payload === 'string') return payload
    if (Array.isArray(payload)) {
      const parts = payload.map(formatApiError).filter(Boolean)
      if (parts.length) return parts.join('; ')
    } else if (payload && typeof payload === 'object') {
      const record = payload as Record<string, unknown>
      for (const key of ['detail', 'message', 'error']) {
        if (record[key]) return formatApiError(record[key])
      }
      const parts = Object.entries(record)
        .map(([field, value]) => `${field}: ${formatApiError(value)}`)
        .filter((part) => !part.endsWith(': '))
      if (parts.length) return parts.join('; ')
    }
    return ''
  }

  // Store the promise for deduplication (only for GET)
  if (cacheKey) {
    const requestPromise = fetchPromise.then(async (response) => {
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Request failed' }))
        const errorMessage = formatApiError(error) || 'Request failed'
        // Same structured error as the non-GET path: callers branch on
        // `status` (an attendee page's 404) and read `data` (a field error).
        const err = Object.assign(new Error(`${response.status}: ${errorMessage}`), {
          status: response.status,
          data: error as Record<string, unknown>,
        })
        if (!expectedErrorStatuses?.includes(response.status)) {
          notifyApiError(err, endpoint, method, response.status)
        }
        throw err
      }
      return response.json()
    }).finally(() => {
      // Clean up after request completes
      inFlightRequests.delete(cacheKey)
    })
    
    inFlightRequests.set(cacheKey, requestPromise)
    return requestPromise as Promise<T>
  }

  const response = await fetchPromise

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }))
    const errorMessage = formatApiError(error) || 'Request failed'
    // Attach structured status + parsed body so callers can react to specific
    // conditions (e.g. a `reauth_required` step-up challenge) without parsing
    // the message string. Message is unchanged for backward compatibility.
    const err = Object.assign(new Error(`${response.status}: ${errorMessage}`), {
      status: response.status,
      data: error as Record<string, unknown>,
    })
    // A step-up challenge or a caller-declared expected status is an expected
    // flow, not a failure — don't spam error tracking with it.
    if (error?.code !== 'reauth_required' && !expectedErrorStatuses?.includes(response.status)) {
      notifyApiError(err, endpoint, method, response.status)
    }
    throw err
  }

  // Handle empty responses (e.g. 204 No Content or empty body)
  const text = await response.text()
  if (!text) {
    return {} as T
  }
  return JSON.parse(text)
}

// Auth token endpoint is different from the API base
async function authenticateRequest(username: string, password: string): Promise<AuthResponse> {
  // Remove trailing '/api' to get the base URL, then add '/api-token-auth/'
  // e.g., 'https://api.eventpay.com/en/api' -> 'https://api.eventpay.com/en/api-token-auth/'
  const baseUrl = API_BASE_URL.endsWith('/api') 
    ? API_BASE_URL.slice(0, -4) 
    : API_BASE_URL
  const url = `${baseUrl}/api-token-auth/`
  
  console.log('Auth URL:', url) // Debug log

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    })

    if (response.ok || response.status === 202) {
      const data = await response.json()
      return {
        success: true,
        token: data.token,
        user: data.user,
      }
    }

    const error = await response.json().catch(() => ({}))
    return {
      success: false,
      error: error.errors?.non_field_errors?.[0] || error.detail || 'Authentication failed',
    }
  } catch (error) {
    if (Bugsnag.isStarted() && error instanceof Error) {
      Bugsnag.notify(error, (event) => {
        event.addMetadata('api', { endpoint: '/api-token-auth/', method: 'POST' })
        event.severity = 'warning'
      })
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
    }
  }
}

export interface GetCurrentUserResponse {
  success: boolean
  user?: User
  permissions?: string[]
  error?: string
}

export interface EventsParams {
  page?: number
  page_size?: number
  limit?: number
  offset?: number
  search?: string
  ordering?: string
  is_active?: boolean
  past?: number  // Set to 1 to get past events
  has_permission?: string
}

export interface OrdersParams {
  page?: number
  page_size?: number
  limit?: number
  offset?: number
  vendor_event__event?: number  // This is the correct parameter name
  user?: number
  vendor_event?: number
  vendor_event__vendor?: number
  status?: string
  status__in?: string
  ordering?: string
  created_at__gte?: string
  created_at__lte?: string
  search?: string
  expand?: string
}

export interface TransactionsParams {
  page?: number
  page_size?: number
  limit?: number
  offset?: number
  event?: number  // This is the correct parameter name
  user?: number
  action?: string
  ordering?: string
  timestamp__gte?: string
  timestamp__lte?: string
  amount?: number
  amount__gt?: number
  search?: string
  expand?: string
}

export interface UsersParams {
  page?: number
  page_size?: number
  limit?: number
  offset?: number
  search?: string
  type?: string
  ordering?: string
  all_users?: number
  expand?: string
}

export interface VendorsParams {
  page?: number
  page_size?: number
  event?: number
  search?: string
  is_active?: boolean
  aggregates?: boolean
  expand?: string
  ordering?: string
}

export interface VendorEventsParams {
  event?: number
  vendor?: number
  search?: string
  aggregates?: boolean
  limit?: number
  offset?: number
  ordering?: string
}

export interface MenuItemsParams {
  page?: number
  page_size?: number
  limit?: number
  offset?: number
  vendor_event?: number
  vendor_event__event?: number
  category?: number
  is_available?: boolean
  is_active?: boolean
  search?: string
  expand?: string
}

export interface VouchersParams {
  page?: number
  page_size?: number
  event?: number
  is_active?: boolean
  is_available?: boolean
  status?: 'all' | 'claimed' | 'unclaimed' | 'expired'
  search?: string
}

export interface ContentPagesParams {
  page?: number
  page_size?: number
  event?: number
  is_published?: boolean
  search?: string
  ordering?: string
}

export interface CreateContentPageData {
  event: number
  title: string
  content: string
  is_published?: boolean
}

export interface VoucherCountsResponse {
  all_vouchers?: number  // Total vouchers including deleted
  active?: number        // Non-deleted vouchers (use this for "All Vouchers" tab)
  claimed: number
  unclaimed: number
  expired: number
  available?: number     // Vouchers still available for claiming
  deleted?: number       // Soft-deleted vouchers
}

// Reporting params and response
export interface ReportingParams {
  aggregation_by?: 'day' | 'hour'
  created_at__gte?: string
  created_at__lte?: string
}

export interface ReportingResponse {
  aggregates?: {
    orders_received?: number
    items_sold?: number
    customers?: number
    tokens?: number
    avg?: number
  }
  vendors?: Array<{
    id?: number
    name: string
    location?: string
    orders_received?: number
    items_sold?: number
    customers?: number
    revenue?: number
  }>
  items?: Array<{
    // API returns these nested field names
    vendor_event_item__name?: string
    vendor_event_item__vendor_event__vendor__name?: string
    // Mapped fields for easier use
    name?: string
    vendor_name?: string
    orders_received?: number
    orders?: number
    items_sold?: number
    customers?: number
    revenue?: number
  }>
  date?: Array<{
    created_at__date: string
    revenue?: number
    orders?: number
    orders_received?: number
    items_sold?: number
    customers?: number
  }>
  locations?: Array<{
    booth?: string
    vendor__name?: string
    vendor_id?: number
    orders_received?: number
    items_sold?: number
    customers?: number
    revenue?: number
  }>
}

// Dashboard stats response from /order/count/
export interface OrderCountResponse {
  statuses?: Array<{ status: string; count: number }>
  amount?: number
  // Main stats
  customers?: number | number[]  // Can be array or number
  total_orders?: number
  items?: number
  tokens?: number  // Tokens issued
  tokens_spent?: number
  avg_order?: number
  // Token summary for more accurate tokens issued
  token_summary?: {
    net_tokens_in_circulation?: number
    total_tokens_created?: number
    total_tokens_spent?: number
    order_refunds?: number
  }
  // Top items - API uses Django ORM field names
  top_5_item?: Array<{
    items__vendor_event_item__name?: string
    vendor_event__vendor__name?: string
    items__vendor_event_item__price?: number
    count?: number
    // Alternative field names
    name?: string
    vendor_name?: string
    tokens?: number
    qty?: number
  }>
  // Top vendors/locations - API uses Django ORM field names
  top_5_vendor?: Array<{
    vendor_event__vendor__name?: string
    vendor_event__booth?: string
    tokens?: number
    amount?: number
    // Alternative field names
    name?: string
    booth?: string
    total_tokens?: number
    revenue?: number
    total_revenue?: number
  }>
  // Time series data for charts
  time_data?: Array<{
    date: string
    amount: number
  }>
}

// Errors thrown by request() (and the multipart helpers) carry the HTTP status.
// Older throw sites only have the `"404: detail"` message, so check both.
export function isNotFoundError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  if ((err as { status?: number }).status === 404) return true
  return err instanceof Error && err.message.startsWith('404')
}

export const apiClient = {
  // Authentication
  authenticateUser: (username: string, password: string): Promise<AuthResponse> => {
    return authenticateRequest(username, password)
  },

  getCurrentUser: async (token: string): Promise<GetCurrentUserResponse> => {
    try {
      const user = await request<User & { permissions?: string[] }>('/user/me/?expand=permissions', { token })
      return {
        success: true,
        user,
        permissions: user.permissions || [],
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get user',
      }
    }
  },

  // Organizations
  getUserOrganizations: (token: string): Promise<{ organizations: Organization[]; count: number }> => {
    return request('/user/me/organizations/', { token })
  },

  // Events - endpoint is /event/
  getEvents: (token: string, params?: EventsParams): Promise<PaginatedResponse<Event>> => {
    return request('/event/', { token, params: { ...params, expand: 'organization' } as ParamsObject })
  },

  getEvent: (token: string, eventId: number): Promise<Event> => {
    return request(`/event/${eventId}/`, { token })
  },

  // Ops create-event flow: `all=1` lifts the default upcoming-only queryset
  // filter so PAST events resolve for duplicate-prefill (superuser-only UI;
  // the endpoint itself is permission-scoped server-side).
  getEventAny: (token: string, eventId: number): Promise<Event> => {
    return request(`/event/${eventId}/`, { token, params: { all: 1 } })
  },

  // Brief list for the "start from past event" picker.
  getEventsBrief: (token: string): Promise<Event[]> => {
    return request('/event/', {
      token,
      params: { all: 1, fields: 'id,name,start_date_time,timezone,currency', limit: 1000 },
    }).then((res) => {
      const rows = (res as PaginatedResponse<Event>).results ?? (res as unknown as Event[])
      return [...rows].sort((a, b) =>
        (b.start_date_time ?? '').localeCompare(a.start_date_time ?? ''))
    })
  },

  getOrganizationsList: (token: string): Promise<Organization[]> => {
    return request('/organization/', { token, params: { limit: 1000 } }).then(
      (res) => (res as PaginatedResponse<Organization>).results ?? (res as unknown as Organization[]),
    )
  },

  createEvent: (token: string, body: Record<string, unknown>): Promise<Event> => {
    return request('/event/', { method: 'POST', token, body })
  },

  // Org attendee CRM list — search/ordering/page are passed straight through;
  // metric ordering (total_spent etc.) is applied server-side in list().
  getOrganizationUsers: (
    token: string,
    orgId: number,
    params: { page?: number; search?: string; ordering?: string } = {},
  ): Promise<OrganizationContactsPage> => {
    const clean = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== ''),
    ) as ParamsObject
    return request(`/organization/${orgId}/users/`, { token, params: clean })
  },

  // Superuser terminal administration (/ops/terminals)
  listPaymentDevices: async (token: string): Promise<AdminPaymentDevice[]> => {
    // DRF default pagination wraps the list; tolerate both shapes.
    const payload = await request<AdminPaymentDevice[] | PaginatedResponse<AdminPaymentDevice>>(
      '/devices/', { token })
    return Array.isArray(payload) ? payload : (payload.results ?? [])
  },

  createPaymentDevice: (token: string, body: Record<string, unknown>): Promise<AdminPaymentDevice> => {
    return request('/devices/', { method: 'POST', token, body })
  },

  updatePaymentDevice: (token: string, deviceId: number, body: Record<string, unknown>): Promise<AdminPaymentDevice> => {
    return request(`/devices/${deviceId}/`, { method: 'PATCH', token, body })
  },

  provisionPaymentDevice: (token: string, deviceId: number): Promise<AdminPaymentDevice> => {
    return request(`/devices/${deviceId}/provision/`, { method: 'POST', token })
  },

  activatePaymentDevice: (token: string, deviceId: number, activationCode?: string): Promise<AdminPaymentDevice> => {
    return request(`/devices/${deviceId}/activate/`, {
      method: 'POST', token, body: activationCode ? { activation_code: activationCode } : {},
    })
  },

  checkPaymentDeviceConnection: (token: string, deviceId: number): Promise<AdminPaymentDevice> => {
    return request(`/devices/${deviceId}/connection/`, { token })
  },

  setPaymentDeviceIdleMessage: (token: string, deviceId: number, message: string): Promise<AdminPaymentDevice> => {
    return request(`/devices/${deviceId}/idle-message/`, { method: 'POST', token, body: { message } })
  },

  // Card-present terminals — MID-filtered per event server-side. The
  // response keeps the legacy {'devices', 'count'} envelope. A 403 is an
  // access answer (no can_use_terminal and not a cashier), not a fault.
  getEventTerminals: async (token: string, eventId: number, checkConnections = false): Promise<EventTerminalDevice[]> => {
    const payload = await request<{ devices: EventTerminalDevice[]; count: number }>(
      `/event/${eventId}/devices/${checkConnections ? '?check_connections=1' : ''}`, { token, expectedErrorStatuses: [403] })
    return payload.devices ?? []
  },

  // Cancel whatever interaction the terminal is running — device-addressed
  // because the reload request is still blocking with the attempt id inside.
  cancelTerminalInteraction: (token: string, eventId: number, deviceId: number): Promise<{ canceled: number }> => {
    return request(`/event/${eventId}/devices/${deviceId}/cancel/`, { method: 'POST', token })
  },

  terminalReload: (
    token: string,
    eventId: number,
    deviceId: number,
    body: { user_id: number; num_tokens: number; idempotency_key: string },
  ): Promise<TerminalAttempt> => {
    return request(`/event/${eventId}/devices/${deviceId}/reload/`, { method: 'POST', token, body })
  },

  // Card phones: staff with can_add_terminal approve each phone (or trust
  // its booth) before it takes cards at the event. Anyone else gets a 403.
  getEventPosGrants: (token: string, eventId: number): Promise<EventPosGrants> => {
    return request(`/event/${eventId}/pos-grants/`, { token, expectedErrorStatuses: [403] })
  },

  // vendor_event: the booth the list showed; the backend refuses
  // (grant_moved) if the phone has since asked from another booth.
  approvePosGrant: (token: string, eventId: number, grantId: number, trustBooth: boolean, vendorEventId: number): Promise<PosGrant> => {
    return request(`/event/${eventId}/pos-grants/${grantId}/approve/`, {
      method: 'POST', token, body: { trust_booth: trustBooth, vendor_event: vendorEventId },
    })
  },

  approvePosGrantByCode: (token: string, eventId: number, code: string, trustBooth: boolean): Promise<PosGrant> => {
    return request(`/event/${eventId}/pos-grants/approve/`, {
      method: 'POST', token, body: { code, trust_booth: trustBooth },
    })
  },

  revokePosGrant: (token: string, eventId: number, grantId: number): Promise<PosGrant> => {
    return request(`/event/${eventId}/pos-grants/${grantId}/revoke/`, { method: 'POST', token })
  },

  setPosBoothTrusted: (token: string, eventId: number, vendorEventId: number, trusted: boolean): Promise<unknown> => {
    return request(`/event/${eventId}/pos-booths/${vendorEventId}/${trusted ? 'trust' : 'untrust'}/`, { method: 'POST', token })
  },

  // SDK-local readers (PAX D135): POST because the response carries the
  // reader's role-scoped credentials.
  getTerminalSdkSession: (token: string, eventId: number, deviceId: number): Promise<TerminalSdkSession> => {
    return request(`/event/${eventId}/devices/${deviceId}/sdk-session/`, { method: 'POST', token, body: {} })
  },

  confirmTerminalAttempt: (
    token: string,
    eventId: number,
    attemptId: number,
    transferId: string,
  ): Promise<TerminalAttempt> => {
    return request(`/event/${eventId}/device-attempts/${attemptId}/confirm/`, {
      method: 'POST', token, body: { transfer_id: transferId },
    })
  },

  cancelTerminalAttempt: (
    token: string,
    eventId: number,
    attemptId: number,
  ): Promise<TerminalAttempt> => {
    return request(`/event/${eventId}/device-attempts/${attemptId}/cancel/`, {
      method: 'POST', token,
    })
  },

  getOrganizationUserEvents: (
    token: string,
    orgId: number,
    userId: number,
  ): Promise<OrganizationContactEvents> => {
    return request(`/organization/${orgId}/users/${userId}/events/`, { token })
  },

  // Organization (read-only for authenticated users). Used by the Toolkit to
  // resolve the org slug for the public group-page link.
  getOrganization: (token: string, orgId: number): Promise<Organization> => {
    return request(`/organization/${orgId}/`, { token })
  },

  // Trackable short links (Toolkit QR/share). POST is create-or-get keyed on
  // (event, source); a changed destination re-points the existing code.
  createOrGetShortLink: (
    token: string,
    body: { event: number; source: string; destination: string; code?: string },
  ): Promise<ShortLink> => {
    return request('/short-links/', { method: 'POST', token, body })
  },

  listShortLinks: (token: string, eventId: number): Promise<ShortLink[]> => {
    return request(`/short-links/?event=${eventId}`, { token })
  },

  updateEvent: (token: string, eventId: number, data: Partial<Event>): Promise<Event> => {
    return request(`/event/${eventId}/`, { method: 'PATCH', token, body: data })
  },

  // Update event with file uploads - uses PATCH with multipart/form-data
  updateEventWithFiles: async (token: string, eventId: number, data: Record<string, unknown>, files?: { banner?: File; logo?: File }): Promise<Event> => {
    const formData = new FormData()
    
    // Append all non-file fields
    for (const [key, value] of Object.entries(data)) {
      if (value !== undefined && value !== null) {
        formData.append(key, typeof value === 'boolean' ? (value ? 'true' : 'false') : String(value))
      }
    }
    
    // Append file fields
    if (files?.banner) formData.append('banner', files.banner)
    if (files?.logo) formData.append('logo', files.logo)
    
    const response = await fetch(`${API_BASE_URL}/event/${eventId}/`, {
      method: 'PATCH',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Token ${token}`,
      },
      body: formData,
    })
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to update event' }))
      throw new Error(error.detail || error.message || 'Failed to update event')
    }
    
    return response.json()
  },

  // Orders - endpoint is /order/ with vendor_event__event parameter
  getOrders: (token: string, eventId: number, params?: Omit<OrdersParams, 'vendor_event__event'>): Promise<PaginatedResponse<Order>> => {
    const fullParams: OrdersParams = {
      ...params,
      vendor_event__event: eventId,
      expand: params?.expand || 'vendor_event,vendor_event.vendor,user',
    }
    return request('/order/', { token, params: fullParams as ParamsObject })
  },

  getOrder: (token: string, orderId: number): Promise<Order> => {
    // transactionlog_set: the order modal shows a card sale's payment.
    return request(`/order/${orderId}/?expand=order_items.vendor_event_item,vendor_event.vendor,statuses,user,user.balance,transactionlog_set`, { token })
  },

  // Transactions - endpoint is /transaction/ with event parameter
  getTransactions: (token: string, eventId: number, params?: Omit<TransactionsParams, 'event'>): Promise<PaginatedResponse<Transaction>> => {
    const fullParams: TransactionsParams = {
      ...params,
      event: eventId,
      expand: params?.expand || 'user,event,from_user,order.vendor_event.vendor,to_user',
    }
    return request('/transaction/', { token, params: fullParams as ParamsObject })
  },

  getTransaction: (token: string, transactionId: number): Promise<Transaction> => {
    return request(`/transaction/${transactionId}/?expand=user,event,from_user,to_user,order.vendor_event.vendor,order.order_items.vendor_event_item,purchased_items`, { token })
  },

  // Users - endpoint is /event/{eventId}/users/
  getEventUsers: (token: string, eventId: number, params?: UsersParams): Promise<PaginatedResponse<User>> => {
    const fullParams: UsersParams = {
      ...params,
      expand: params?.expand || 'balance,activation_code',
    }
    return request(`/event/${eventId}/users/`, { token, params: fullParams as ParamsObject })
  },
  getUserByQrCode: (token: string, qrCode: string): Promise<User> => {
    return request(`/user/by-qrcode/`, {
      token,
      params: { qrcode: qrCode },
    })
  },

  // Get user details
  getUser: (token: string, userId: number): Promise<User> => {
    return request(`/user/${userId}/`, { token })
  },

  // Create user (POST /user/)
  createUser: (token: string, data: {
    event: number
    first_name: string
    last_name: string
    email?: string
    phone_number?: string
    password?: string
    confirm_password?: string
    is_active?: boolean
  }): Promise<User> => {
    return request('/user/', { method: 'POST', token, body: { is_active: true, ...data } })
  },

  // Update user (PATCH /user/{userId}/)
  updateUser: (token: string, userId: number, data: { first_name?: string; last_name?: string; email?: string }): Promise<User> => {
    return request(`/user/${userId}/`, { method: 'PATCH', token, body: data })
  },

  // Get user balance for event
  getUserBalance: (token: string, userId: number, eventId: number): Promise<{ balance: number }> => {
    return request(`/user/${userId}/balance/?event=${eventId}`, { token })
  },

  // Vendors - endpoint is /vendor/
  createVendor: async (token: string, data: { 
    name: string
    description?: string
    website?: string
    phone?: string
    event?: number
    is_approved?: boolean
    logo?: File
    background?: File
  }): Promise<Vendor> => {
    const formData = new FormData()
    formData.append('name', data.name)
    if (data.description) formData.append('description', data.description)
    if (data.website) formData.append('website', data.website)
    if (data.phone) formData.append('phone', data.phone)
    if (data.event) formData.append('event', String(data.event))
    if (data.is_approved != null) formData.append('is_approved', String(data.is_approved))
    if (data.logo) formData.append('logo', data.logo)
    if (data.background) formData.append('background', data.background)
    
    const response = await fetch(`${API_BASE_URL}/vendor/`, {
      method: 'POST',
      headers: {
        'Authorization': `Token ${token}`,
      },
      body: formData,
    })
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to create vendor' }))
      throw new Error(error.detail || error.message || 'Failed to create vendor')
    }
    
    return response.json()
  },

  getVendors: (token: string, params?: VendorsParams): Promise<PaginatedResponse<Vendor>> => {
    const fullParams = {
      ...params,
      expand: params?.expand || 'vendorevent_set,permissions',
      aggregates: params?.aggregates !== false ? 1 : undefined,
    }
    return request('/vendor/', { token, params: fullParams as ParamsObject })
  },

  getVendor: (token: string, vendorId: number, params?: { expand?: string; aggregates?: boolean; all_items?: boolean; event?: number; created_at__gte?: string; created_at__lte?: string }): Promise<Vendor> => {
    const queryParams: ParamsObject = {}
    if (params?.expand) queryParams.expand = params.expand
    if (params?.aggregates !== false) queryParams.aggregates = 1
    if (params?.all_items) queryParams.all_items = 1
    if (params?.event) queryParams.event = params.event
    if (params?.created_at__gte) queryParams.created_at__gte = params.created_at__gte
    if (params?.created_at__lte) queryParams.created_at__lte = params.created_at__lte
    return request(`/vendor/${vendorId}/`, { token, params: queryParams })
  },

  // Update vendor - uses PATCH with multipart/form-data
  updateVendor: async (token: string, vendorId: number, data: {
    name?: string
    description?: string
    website?: string
    phone?: string
    is_approved?: boolean
    logo?: File | null
    background?: File | null
  }): Promise<Vendor> => {
    const formData = new FormData()
    if (data.name !== undefined) formData.append('name', data.name)
    if (data.description !== undefined) formData.append('description', data.description)
    if (data.website !== undefined) formData.append('website', data.website)
    if (data.phone !== undefined) formData.append('phone', data.phone)
    if (data.is_approved !== undefined) formData.append('is_approved', String(data.is_approved))
    if (data.logo instanceof File) formData.append('logo', data.logo)
    else if (data.logo === null) formData.append('logo', '')
    if (data.background instanceof File) formData.append('background', data.background)
    else if (data.background === null) formData.append('background', '')
    
    const response = await fetch(`${API_BASE_URL}/vendor/${vendorId}/`, {
      method: 'PATCH',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Token ${token}`,
      },
      body: formData,
    })
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to update vendor' }))
      throw new Error(error.detail || error.message || 'Failed to update vendor')
    }
    
    return response.json()
  },

  // Vendor stats - endpoint is /order/count/
  getVendorStats: (token: string, vendorId: number, eventId: number, params?: { start?: string; end?: string }): Promise<VendorStats> => {
    const queryParams: ParamsObject = {
      vendor_event__vendor: vendorId,
      vendor_event__event: eventId,
    }
    if (params?.start) queryParams.created_at__gte = params.start
    if (params?.end) queryParams.created_at__lte = params.end
    return request('/order/count/', { token, params: queryParams })
  },

  // Vendor count stats - new endpoint with top items and hourly data
  getVendorCountStats: (token: string, vendorId: number, eventId: number, params?: { top_count?: number }): Promise<VendorCountStats> => {
    const queryParams: ParamsObject = {
      event: eventId,
    }
    if (params?.top_count) queryParams.top_count = params.top_count
    return request(`/vendor/${vendorId}/count/`, { token, params: queryParams })
  },

  // Vendor locations by event
  getVendorLocations: (token: string, vendorId: number, eventId: number): Promise<PaginatedResponse<VendorEvent>> => {
    return request('/vendor-event/', { 
      token, 
      params: { 
        vendor: vendorId, 
        event: eventId, 
        aggregates: 1 
      } as ParamsObject 
    })
  },

  // Vendor Events (Locations) - endpoint is /vendor-event/
  getVendorEvents: (
    token: string, eventId: number, params?: VendorEventsParams, options?: { expectedErrorStatuses?: number[] },
  ): Promise<PaginatedResponse<VendorEvent>> => {
    const fullParams = {
      ...params,
      event: eventId,
      expand: 'vendor',
      aggregates: params?.aggregates !== false ? 1 : undefined,
    }
    return request('/vendor-event/', { token, params: fullParams as ParamsObject, ...options })
  },

  getVendorEvent: (token: string, vendorEventId: number, params?: { expand?: string; aggregates?: boolean; event?: number }): Promise<VendorEvent> => {
    const queryParams: ParamsObject = {}
    if (params?.expand) queryParams.expand = params.expand
    if (params?.aggregates !== false) queryParams.aggregates = 1
    if (params?.event) queryParams.event = params.event
    return request(`/vendor-event/${vendorEventId}/`, { token, params: queryParams })
  },

  // Vendor Event (Location) count stats - with top items and hourly data
  getVendorEventCountStats: (token: string, vendorEventId: number, params?: { top_count?: number; event?: number }): Promise<VendorEventCountStats> => {
    const queryParams: ParamsObject = {}
    if (params?.top_count) queryParams.top_count = params.top_count
    if (params?.event) queryParams.event = params.event
    return request(`/vendor-event/${vendorEventId}/count/`, { token, params: queryParams })
  },

  createVendorEvent: (token: string, data: Partial<VendorEvent>): Promise<VendorEvent> => {
    return request('/vendor-event/', { method: 'POST', token, body: data })
  },

  updateVendorEvent: (token: string, vendorEventId: number, data: Partial<VendorEvent>): Promise<VendorEvent> => {
    return request(`/vendor-event/${vendorEventId}/`, { method: 'PATCH', token, body: data })
  },

  // Update vendor event with file uploads - uses PATCH with multipart/form-data
  updateVendorEventWithFiles: async (
    token: string,
    vendorEventId: number,
    data: Record<string, unknown>,
    files?: { logo?: File | null; background?: File | null }
  ): Promise<VendorEvent> => {
    const formData = new FormData()
    for (const [key, value] of Object.entries(data)) {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          // DRF multipart reads M2M values via getlist — one entry per item
          value.forEach((v) => formData.append(key, String(v)))
        } else {
          formData.append(key, typeof value === 'boolean' ? (value ? 'true' : 'false') : String(value))
        }
      }
    }
    if (files?.logo instanceof File) formData.append('logo', files.logo)
    else if (files?.logo === null) formData.append('logo', '')
    if (files?.background instanceof File) formData.append('background', files.background)
    else if (files?.background === null) formData.append('background', '')

    const response = await fetch(`${API_BASE_URL}/vendor-event/${vendorEventId}/`, {
      method: 'PATCH',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Token ${token}`,
      },
      body: formData,
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to update location' }))
      // Match request()'s error shape so callers can branch on `status`.
      throw Object.assign(
        new Error(`${response.status}: ${error.detail || error.message || 'Failed to update location'}`),
        { status: response.status, data: error },
      )
    }

    return response.json()
  },

  deleteVendorEvent: (token: string, vendorEventId: number): Promise<void> => {
    return request(`/vendor-event/${vendorEventId}/`, { method: 'DELETE', token })
  },

  // Location stats - endpoint is /order/count/
  getLocationStats: (token: string, vendorEventId: number, params?: { start?: string; end?: string; all_items?: boolean; created_at__gte?: string; created_at__lte?: string }): Promise<VendorStats> => {
    const queryParams: ParamsObject = {
      vendor_event: vendorEventId,
    }
    if (params?.start) queryParams.start = params.start
    if (params?.end) queryParams.end = params.end
    if (params?.all_items) queryParams.all_items = 1
    // Date window applied by the order filterset (created_at gte/lte)
    if (params?.created_at__gte) queryParams.created_at__gte = params.created_at__gte
    if (params?.created_at__lte) queryParams.created_at__lte = params.created_at__lte
    return request('/order/count/', { token, params: queryParams })
  },

  // Menu Items - endpoint is /vendor-event-item/
  getMenuItems: (token: string, params?: MenuItemsParams): Promise<PaginatedResponse<MenuItem>> => {
    const fullParams: MenuItemsParams = {
      ...params,
      expand: params?.expand || 'vendor_event.vendor',
    }
    return request('/vendor-event-item/', { token, params: fullParams as ParamsObject })
  },

  getMenuItem: (token: string, itemId: number): Promise<MenuItem> => {
    return request(`/vendor-event-item/${itemId}/?expand=vendor_event.vendor`, { token })
  },

  createMenuItem: (token: string, data: Partial<MenuItem>): Promise<MenuItem> => {
    return request('/vendor-event-item/', { method: 'POST', token, body: data })
  },

  updateMenuItem: (token: string, itemId: number, data: Partial<MenuItem>): Promise<MenuItem> => {
    return request(`/vendor-event-item/${itemId}/`, { method: 'PATCH', token, body: data })
  },

  deleteMenuItem: (token: string, itemId: number): Promise<void> => {
    return request(`/vendor-event-item/${itemId}/`, { method: 'DELETE', token })
  },

  // Vouchers - endpoint is /token-voucher/
  getVouchers: (token: string, params?: VouchersParams): Promise<PaginatedResponse<Voucher>> => {
    // Convert status to API filter parameters
    const { status, ...restParams } = params || {}
    const apiParams: ParamsObject = { ...restParams, expand: 'created_by' }
    
    if (status === 'claimed') {
      apiParams.times_claimed__gt = 0
    } else if (status === 'unclaimed') {
      apiParams.times_claimed = 0
    } else if (status === 'expired') {
      // Filter by expiry date less than now
      apiParams.expiry__lt = new Date().toISOString()
    }
    
    return request('/token-voucher/', { token, params: apiParams })
  },

  getVoucher: (token: string, voucherId: number): Promise<Voucher> => {
    return request(`/token-voucher/${voucherId}/?expand=created_by`, { token })
  },

  createVoucher: (token: string, data: Partial<Voucher>): Promise<Voucher> => {
    return request('/token-voucher/', { method: 'POST', token, body: data })
  },

  updateVoucher: (token: string, voucherId: number, data: Partial<Voucher>): Promise<Voucher> => {
    return request(`/token-voucher/${voucherId}/`, { method: 'PATCH', token, body: data })
  },

  deleteVoucher: (token: string, voucherId: number): Promise<void> => {
    return request(`/token-voucher/${voucherId}/`, { method: 'DELETE', token })
  },

  assignVoucher: (token: string, voucherId: number, userId: number): Promise<{ success: boolean; message: string; voucher: Voucher }> => {
    return request(`/token-voucher/${voucherId}/assign/`, { method: 'POST', token, body: { user_id: userId } })
  },

  getVoucherCounts: (token: string, eventId: number): Promise<VoucherCountsResponse> => {
    // Use the voucher-stats endpoint which returns claimed, unclaimed, expired, active counts
    return request(`/event/${eventId}/voucher-stats/`, { token })
  },

  bulkImportVouchers: (token: string, eventId: number, file: File): Promise<{ imported: number; errors: string[] }> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('event', String(eventId))
    return request('/token-voucher/bulk-import/', { 
      method: 'POST', 
      token, 
      body: formData,
      headers: {} // Let browser set Content-Type for FormData
    })
  },

  // Dashboard - endpoint is /order/count/ with vendor_event__event
  getDashboardStats: (token: string, eventId: number, params?: { created_at__gte?: string; created_at__lte?: string }): Promise<OrderCountResponse> => {
    const fullParams = {
      vendor_event__event: eventId,
      ...params,
    }
    return request('/order/count/', { token, params: fullParams as ParamsObject })
  },

  // Reporting - endpoint is /event/{event_id}/reporting/
  getReporting: (token: string, eventId: number, params?: ReportingParams): Promise<ReportingResponse> => {
    const fullParams = {
      aggregation_by: 'day',
      ...params,
    }
    return request(`/event/${eventId}/reporting/`, { token, params: fullParams as ParamsObject })
  },

  // Event Insights Stats - endpoint is /event/{event_id}/stats/
  getEventInsightsStats: (token: string, eventId: number, params?: { top_count?: number; start_date?: string; end_date?: string }): Promise<EventInsightsStats> => {
    return request(`/event/${eventId}/stats/`, { token, params: params as ParamsObject })
  },

  // Staff reloads by payment method (cash/standalone/terminal/manual)
  getSupportReloads: (
    token: string,
    eventId: number,
    params?: { created_at__gte?: string; created_at__lte?: string; staff?: string },
  ): Promise<SupportReloadsReport> => {
    return request(`/event/${eventId}/financials/support-reloads/`, {
      token, params: params as ParamsObject,
    })
  },

  // Financials - endpoint is /event/{event_id}/financials/
  getFinancials: (token: string, eventId: number, params?: { created_at__gte?: string; created_at__lte?: string }): Promise<FinancialsResponse> => {
    const queryParams = {
      expand: 'user',
      ...params,
    }
    return request(`/event/${eventId}/financials/`, { token, params: queryParams as ParamsObject })
  },

  // Get transaction count
  getTransactionCount: (token: string, eventId: number): Promise<{ count: number }> => {
    return request(`/transaction/count/?event=${eventId}`, { token })
  },

  // Reload / Add Credit. The description is what lets financials tell cash
  // apart from standalone-card staff reloads — always send it.
  addCredit: (token: string, userId: number, eventId: number, amount: number, description?: string): Promise<Transaction> => {
    return request(`/transaction/`, {
      method: 'POST',
      token,
      // Fraud controls answer a blocked or refused attendee with 403/402
      // {code, detail}: a decision for the operator, not an error to report.
      expectedErrorStatuses: [402, 403],
      body: {
        user: userId,
        event: eventId,
        amount,
        ...(description ? { description } : {}),
      },
    })
  },

  // Transfer
  transferTokens: (token: string, eventId: number, fromUserId: number, toUserId: number, amount: number): Promise<{ from_transaction: Transaction; to_transaction: Transaction }> => {
    return request(`/transaction/transfer/`, {
      method: 'POST',
      token,
      body: { from_user: fromUserId, to_user: toUserId, amount, event: eventId },
    })
  },

  // Generate Login Code for inactive users
  generateLoginCode: (token: string, userId: number, eventId: number): Promise<{ code: string }> => {
    return request(`/user/${userId}/generate-login-code/`, {
      method: 'POST',
      token,
      body: { event_id: eventId },
    })
  },

  // Step-up re-authentication. Verifies the current user's password and opens
  // the short-lived window required for destructive actions (e.g. refunds).
  reauth: (token: string, password: string): Promise<{ ok: boolean; window_seconds: number }> => {
    return request('/reauth/', {
      method: 'POST',
      token,
      body: { password },
    })
  },

  // Refund Transaction
  refundTransaction: (token: string, transactionId: number, amount?: number): Promise<Transaction> => {
    return request(`/transaction/${transactionId}/refund/`, {
      method: 'POST',
      token,
      body: amount !== undefined ? { amount } : undefined,
    })
  },

  // Refund Order — full-line refunds by item IDs.
  // Backend refunds the entire qty of each OrderItem; no per-unit granularity.
  refundOrderItems: (token: string, orderId: number, itemIds: number[]): Promise<void> => {
    return request(`/order/${orderId}/refund-items/`, {
      method: 'POST',
      token,
      body: itemIds,
    })
  },

  // Refund Order — partial refund by token amount.
  // Use this when the user wants to refund less than a full line item
  // (e.g. 1 of 3 wines on a single line). Amount must be <= order.total.
  //
  // `event_id` is REQUIRED in the body even though the URL is order-scoped:
  // the backend's HasEventPermission(['can_refund_order']) checks for event_id
  // in path/body/query and 403s without it. Non-staff EventAdmins / OrgAdmins
  // get denied if we omit it. See orders/views.py refund_partial.
  refundOrderPartial: (token: string, orderId: number, eventId: number, amount: number): Promise<Order> => {
    return request(`/order/${orderId}/refund-partial/`, {
      method: 'POST',
      token,
      body: { amount, event_id: eventId },
    })
  },

  // Email Receipt
  emailOrderReceipt: (token: string, eventId: number, orderId: number): Promise<{ success: boolean; message?: string }> => {
    return request(`/event/${eventId}/orders/${orderId}/email-receipt/`, {
      method: 'POST',
      token,
    })
  },
  emailTransactionReceipt: (token: string, eventId: number, transactionId: number): Promise<{ success: boolean; message?: string }> => {
    return request(`/event/${eventId}/transactions/${transactionId}/email-receipt/`, {
      method: 'POST',
      token,
    })
  },

  // Password Reset - uses direct fetch with specific headers matching old project
  sendPasswordResetCode: async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/send-code/email/`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
          'Origin': FRONTEND_URL,
        },
        body: JSON.stringify({ email, template: 'reset' }),
      })
      
      if (response.ok || response.status === 202) {
        return { success: true }
      }
      
      const error = await response.json().catch(() => ({ detail: 'Failed to send reset code' }))
      return {
        success: false,
        error: error.detail || error.error || error.message || 'Failed to send reset code',
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send reset code',
      }
    }
  },

  // Resend an invitee's activation e-mail. Same endpoint as the reset code; the
  // backend regenerates the user's activation code and e-mails the link itself,
  // so Studio never needs the code. Answers 2xx whether or not the address
  // exists (enumeration-safe) and 429 when rate-limited per address
  // (1 per 30 s, 10 per hour).
  sendActivationCode: async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/send-code/email/`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
          'Origin': FRONTEND_URL,
        },
        body: JSON.stringify({ email, template: 'activate' }),
      })

      if (response.ok || response.status === 202) {
        return { success: true }
      }

      if (response.status === 429) {
        return { success: false, error: 'Invite already sent, try again in a moment' }
      }

      const error = await response.json().catch(() => ({ detail: 'Failed to send invite' }))
      return {
        success: false,
        error: error.detail || error.error || error.message || 'Failed to send invite',
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send invite',
      }
    }
  },

  verifyPasswordResetCode: async (
    email: string,
    code: string,
    password: string,
    firstName?: string,
    lastName?: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const body: Record<string, string | number> = { email, code: parseInt(code), password }
      if (firstName) body.first_name = firstName
      if (lastName) body.last_name = lastName
      
      const response = await fetch(`${API_BASE_URL}/verify-code/email/`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
          'Origin': FRONTEND_URL,
        },
        body: JSON.stringify(body),
      })
      
      if (response.ok || response.status === 202) {
        return { success: true }
      }
      
      const error = await response.json().catch(() => ({ detail: 'Failed to verify code' }))
      return {
        success: false,
        error: error.detail || error.error || error.message || 'Failed to verify code',
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to verify code',
      }
    }
  },

  // Event Content Pages
  getContentPages: (token: string, eventId: number, params?: ContentPagesParams): Promise<PaginatedResponse<ContentPage>> => {
    const fullParams = {
      ...params,
      event: eventId,
    }
    return request('/event-content-page/', { token, params: fullParams as ParamsObject })
  },

  getContentPage: (token: string, pageId: number): Promise<ContentPage> => {
    return request(`/event-content-page/${pageId}/`, { token })
  },

  createContentPage: (token: string, data: CreateContentPageData): Promise<ContentPage> => {
    return request('/event-content-page/', { method: 'POST', token, body: data })
  },

  updateContentPage: (token: string, pageId: number, data: Partial<CreateContentPageData>): Promise<ContentPage> => {
    return request(`/event-content-page/${pageId}/`, { method: 'PATCH', token, body: data })
  },

  deleteContentPage: (token: string, pageId: number): Promise<void> => {
    return request(`/event-content-page/${pageId}/`, { method: 'DELETE', token })
  },

  // User-specific data for User Modal
  // Fetches metrics from the event-stats API endpoint
  getUserTransactionMetrics: async (token: string, userId: number, eventId: number, tokenValue: number = 2.0): Promise<UserTransactionMetrics> => {
    try {
      // Use the event-stats endpoint which returns pre-calculated metrics
      const statsData = await request<{
        total_reloaded?: number
        total_spent?: number
        transfers_received?: number
        transfers_sent?: number
        total_refunded?: number
        balance?: number
      }>(`/user/${userId}/event-stats/${eventId}/`, { token })
      
      const purchased_tokens = statsData.total_reloaded || 0
      const spent_tokens = statsData.total_spent || 0
      const received_tokens = statsData.transfers_received || 0
      const sent_tokens = statsData.transfers_sent || 0
      const refunded_tokens = statsData.total_refunded || 0
      const balance_tokens = statsData.balance || 0
      
      return {
        purchased_tokens,
        purchased_dollars: Math.round(purchased_tokens * tokenValue),
        spent_tokens,
        spent_dollars: Math.round(spent_tokens * tokenValue),
        received_tokens,
        received_dollars: Math.round(received_tokens * tokenValue),
        sent_tokens,
        sent_dollars: Math.round(sent_tokens * tokenValue),
        refunded_tokens,
        refunded_dollars: Math.round(refunded_tokens * tokenValue),
        balance_tokens,
        balance_dollars: Math.round(balance_tokens * tokenValue),
      }
    } catch (err) {
      console.error('Error fetching user transaction metrics:', err)
      // Return zeros if calculation fails
      return {
        purchased_tokens: 0, purchased_dollars: 0,
        spent_tokens: 0, spent_dollars: 0,
        received_tokens: 0, received_dollars: 0,
        sent_tokens: 0, sent_dollars: 0,
        refunded_tokens: 0, refunded_dollars: 0,
        balance_tokens: 0, balance_dollars: 0,
      }
    }
  },

  getUserEventStats: async (token: string, userId: number, eventId: number): Promise<UserEventStats> => {
    try {
      // Fetch transactions to find first/last transaction dates
      const transactionsData = await request<PaginatedResponse<any>>('/transaction/', {
        token,
        params: {
          user: userId,
          event: eventId,
          ordering: 'created_at', // Oldest first
          page_size: 1,
        } as ParamsObject,
      })
      
      const firstTransaction = transactionsData.results?.[0]
      
      // Fetch orders to get total orders and last order
      const ordersData = await request<PaginatedResponse<any>>('/order/', {
        token,
        params: {
          user: userId,
          vendor_event__event: eventId,
          ordering: '-created_at', // Newest first
          page_size: 1,
        } as ParamsObject,
      })
      
      const lastOrder = ordersData.results?.[0]
      
      return {
        first_transaction_at: firstTransaction?.created_at,
        last_transaction_at: undefined, // Could fetch separately if needed
        total_orders: ordersData.count || 0,
        total_spent: 0, // Calculated elsewhere
        last_order_date: lastOrder?.created_at,
      }
    } catch (err) {
      console.error('Error fetching user event stats:', err)
      return {
        total_orders: 0,
        total_spent: 0,
      }
    }
  },

  getUserOrders: (token: string, userId: number, eventId: number, params?: { page?: number; page_size?: number; limit?: number; offset?: number; search?: string }): Promise<PaginatedResponse<Order>> => {
    return request('/order/', {
      token,
      params: {
        user: userId,
        vendor_event__event: eventId,
        expand: 'vendor_event.vendor',
        ordering: '-created_at',
        ...params,
      } as ParamsObject,
    })
  },

  getUserTransactions: (token: string, userId: number, eventId: number, params?: { page?: number; page_size?: number; limit?: number; offset?: number; search?: string; action?: string }): Promise<PaginatedResponse<Transaction>> => {
    return request('/transaction/', {
      token,
      params: {
        user: userId,
        event: eventId,
        expand: 'order.vendor_event.vendor,from_user,to_user,card',
        ordering: '-timestamp',
        ...params,
      } as ParamsObject,
    })
  },

  getUserRedeemableItems: async (token: string, userId: number, eventId: number, params?: { page?: number; page_size?: number; status?: string }): Promise<PaginatedResponse<RedeemableItem>> => {
    try {
      return await request(`/redeemable-item/`, {
        token,
        params: { ...params, event: eventId, user: userId } as ParamsObject,
      })
    } catch {
      return { count: 0, next: null, previous: null, results: [] }
    }
  },

  // QR Codes / Wallet Passes - uses /user/{user_id}/event/{event_id}/qr-code/ endpoints
  getUserWalletPasses: async (token: string, userId: number, eventId: number): Promise<WalletPass[]> => {
    try {
      const data = await request<any>(`/user/${userId}/event/${eventId}/qr-code/`, { token })
      // API returns array directly or wrapped in results
      return Array.isArray(data) ? data : (data.results || [])
    } catch (err) {
      console.error('Error fetching QR codes:', err)
      return []
    }
  },

  addUserWalletPass: async (token: string, userId: number, eventId: number, barcode: string): Promise<WalletPass> => {
    // API expects { "code": barcode } and may return empty response on success
    const url = `${API_BASE_URL}/user/${userId}/event/${eventId}/qr-code/add/`
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Token ${token}`,
      },
      body: JSON.stringify({ code: barcode }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to add QR code' }))
      throw new Error(error.detail || error.message || 'Failed to add QR code')
    }

    // API may return empty response on success (200/201/204)
    const text = await response.text()
    if (!text) {
      // Return a minimal success object
      return { id: 0, barcode, event: { id: eventId, name: '' }, user: userId, created: new Date().toISOString() }
    }
    
    try {
      return JSON.parse(text)
    } catch {
      // If JSON parsing fails but request was successful, return minimal object
      return { id: 0, barcode, event: { id: eventId, name: '' }, user: userId, created: new Date().toISOString() }
    }
  },

  deleteUserWalletPass: async (token: string, userId: number, eventId: number, barcodeId: string): Promise<void> => {
    // API may return empty response on success
    const url = `${API_BASE_URL}/user/${userId}/event/${eventId}/qr-code/${barcodeId}/delete/`
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Token ${token}`,
      },
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to delete QR code' }))
      throw new Error(error.detail || error.message || 'Failed to delete QR code')
    }
    // Success - no need to parse response
  },

  // Vendor Administrators - endpoint is /vendor-administrator/
  getVendorAdministrators: (token: string, vendorId: number): Promise<PaginatedResponse<VendorAdministrator>> => {
    return request('/vendor-administrator/', { token, params: { vendor: vendorId, expand: 'user,groups' } as ParamsObject })
  },

  createVendorAdministrator: async (token: string, data: { 
    email: string
    vendor: number
    groups: number[]
    add_to_past_events?: boolean
    vendor_event_ids?: number[]
    event_id?: number
  }): Promise<VendorAdministrator> => {
    return request('/vendor-administrator/', { method: 'POST', token, body: data })
  },

  updateVendorAdministrator: async (token: string, adminId: number, data: {
    groups?: number[]
    vendor_event_ids?: number[]
    event_id?: number
  }): Promise<VendorAdministrator> => {
    return request(`/vendor-administrator/${adminId}/`, { method: 'PATCH', token, body: data })
  },
  
  // Get all vendor events for a vendor (all events)
  getVendorEventsByVendor: (token: string, vendorId: number): Promise<PaginatedResponse<VendorEvent>> => {
    return request('/vendor-event/', { 
      token, 
      params: { 
        vendor: vendorId,
        expand: 'event,vendor',
        limit: 100
      } as ParamsObject
    })
  },

  deleteVendorAdministrator: async (token: string, adminId: number, eventId?: number): Promise<void> => {
    return request(`/vendor-administrator/${adminId}/`, {
      method: 'DELETE',
      token,
      params: eventId ? { event_id: eventId } : undefined,
    })
  },

  // Vendor Event Administrators (Location Users) - endpoint is /vendor-event-administrator/
  getVendorEventAdministrators: (token: string, vendorEventId: number, eventId?: number): Promise<PaginatedResponse<VendorEventAdministrator>> => {
    const params: ParamsObject = { 
      vendor_event: vendorEventId, 
      expand: 'user,groups' 
    }
    if (eventId) params.event = eventId
    return request('/vendor-event-administrator/', { token, params })
  },

  createVendorEventAdministrator: async (token: string, data: { email: string; vendor_event: number; groups: number[] }): Promise<VendorEventAdministrator> => {
    return request('/vendor-event-administrator/', { method: 'POST', token, body: data })
  },

  updateVendorEventAdministrator: async (token: string, adminId: number, data: { groups: number[] }): Promise<VendorEventAdministrator> => {
    return request(`/vendor-event-administrator/${adminId}/`, { method: 'PATCH', token, body: data })
  },

  deleteVendorEventAdministrator: async (token: string, adminId: number): Promise<void> => {
    return request(`/vendor-event-administrator/${adminId}/`, { method: 'DELETE', token })
  },

  // Event Administrators - endpoint is /event-administrator/
  getEventAdministrators: (token: string, eventId: number): Promise<PaginatedResponse<EventAdministrator>> => {
    return request('/event-administrator/', {
      token,
      params: {
        event: eventId,
        expand: 'user,groups'
      } as ParamsObject
    })
  },

  // Membership probe: the endpoint always exposes the caller's own records,
  // so any authenticated user can check their own membership for an event.
  getMyEventAdministratorRecords: (token: string, eventId: number, userId: number): Promise<PaginatedResponse<EventAdministrator>> => {
    return request('/event-administrator/', {
      token,
      params: { event: eventId, user: userId } as ParamsObject,
    })
  },

  // Same self-probe for organization admins. Non-org-admins get a 403
  // (IsAdminThisOrg), which callers must treat as "no record" — it's the
  // expected answer, so it is excluded from error tracking.
  getMyOrganizationAdministratorRecords: (token: string, organizationId: number, userId: number): Promise<PaginatedResponse<{ id: number }>> => {
    return request('/organization-administrator/', {
      token,
      params: { organization: organizationId, user: userId } as ParamsObject,
      expectedErrorStatuses: [403],
    })
  },

  createEventAdministrator: async (token: string, data: { email: string; event: number; groups: number[] }): Promise<EventAdministrator> => {
    // API expects group_ids, not groups
    const payload = {
      email: data.email,
      event: data.event,
      group_ids: data.groups,
    }
    return request('/event-administrator/', { method: 'POST', token, body: payload })
  },

  updateEventAdministrator: async (token: string, adminId: number, data: { group_ids: number[] }): Promise<EventAdministrator> => {
    return request(`/event-administrator/${adminId}/`, { method: 'PATCH', token, body: data })
  },

  deleteEventAdministrator: async (token: string, adminId: number): Promise<void> => {
    return request(`/event-administrator/${adminId}/`, { method: 'DELETE', token })
  },

  // Permission Groups - endpoint is /group/
  getPermissionGroups: (token: string): Promise<PaginatedResponse<PermissionGroup>> => {
    return request('/group/', { token, params: { page_size: 100 } as ParamsObject })
  },

  // Export endpoints - these trigger file downloads
  exportOrders: async (token: string, eventId: number, params?: {
    search?: string
    status?: string
    status__in?: string
    ordering?: string
    start_date?: string
    end_date?: string
  }): Promise<void> => {
    const url = new URL(`${API_BASE_URL}/event/${eventId}/export/orders/`)

    if (params?.search) url.searchParams.append('search', params.search)
    if (params?.status) url.searchParams.append('status', params.status)
    if (params?.status__in) url.searchParams.append('status__in', params.status__in)
    if (params?.ordering) url.searchParams.append('ordering', params.ordering)
    // Export endpoint date-filters on start_date/end_date (YYYY-MM-DD).
    if (params?.start_date) url.searchParams.append('start_date', params.start_date)
    if (params?.end_date) url.searchParams.append('end_date', params.end_date)
    
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Token ${token}`,
      },
    })
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Export failed' }))
      throw new Error(error.detail || error.message || 'Export failed')
    }
    
    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition')
    let filename = `orders_event_${eventId}_${new Date().toISOString().slice(0, 10)}.csv`
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/)
      if (match) filename = match[1]
    }
    
    // Trigger download
    const blob = await response.blob()
    const downloadUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = downloadUrl
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(downloadUrl)
  },

  exportTransactions: async (token: string, eventId: number, params?: {
    search?: string
    action?: string
    ordering?: string
    start_date?: string
    end_date?: string
  }): Promise<void> => {
    const url = new URL(`${API_BASE_URL}/event/${eventId}/export/transactions/`)

    if (params?.search) url.searchParams.append('search', params.search)
    if (params?.action) url.searchParams.append('type', params.action)
    if (params?.ordering) url.searchParams.append('ordering', params.ordering)
    // Export endpoint date-filters on start_date/end_date (YYYY-MM-DD).
    if (params?.start_date) url.searchParams.append('start_date', params.start_date)
    if (params?.end_date) url.searchParams.append('end_date', params.end_date)
    
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Token ${token}`,
      },
    })
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Export failed' }))
      throw new Error(error.detail || error.message || 'Export failed')
    }
    
    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition')
    let filename = `transactions_event_${eventId}_${new Date().toISOString().slice(0, 10)}.csv`
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/)
      if (match) filename = match[1]
    }
    
    // Trigger download
    const blob = await response.blob()
    const downloadUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = downloadUrl
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(downloadUrl)
  },

  exportUsers: async (token: string, eventId: number, params?: { 
    search?: string
    type?: string
    status?: string
    all_users?: boolean
  }): Promise<void> => {
    const url = new URL(`${API_BASE_URL}/event/${eventId}/export/users/`)
    
    if (params?.search) url.searchParams.append('search', params.search)
    if (params?.type) url.searchParams.append('type', params.type)
    if (params?.status) url.searchParams.append('status', params.status)
    if (params?.all_users) url.searchParams.append('all_users', '1')
    
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Token ${token}`,
      },
    })
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Export failed' }))
      throw new Error(error.detail || error.message || 'Export failed')
    }
    
    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition')
    let filename = `users_event_${eventId}_${new Date().toISOString().slice(0, 10)}.csv`
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/)
      if (match) filename = match[1]
    }
    
    // Trigger download
    const blob = await response.blob()
    const downloadUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = downloadUrl
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(downloadUrl)
  },

  // ============================================
  // Event Home Page APIs
  // ============================================

  getEventHomePages: (token: string, eventId: number, params?: { expand?: string }): Promise<PaginatedResponse<EventHomePage>> => {
    const queryParams: ParamsObject = {
      event: eventId,
    }
    if (params?.expand) queryParams.expand = params.expand
    return request('/event-homepage/', { token, params: queryParams })
  },

  getEventHomePage: (token: string, homePageId: number, params?: { expand?: string }): Promise<EventHomePage> => {
    const queryParams: ParamsObject = {}
    if (params?.expand) queryParams.expand = params.expand
    return request(`/event-homepage/${homePageId}/`, { token, params: queryParams })
  },

  createEventHomePage: (token: string, data: { event: number }): Promise<EventHomePage> => {
    return request('/event-homepage/', { method: 'POST', token, body: data })
  },

  updateEventHomePage: (token: string, homePageId: number, data: Partial<{ event: number }>): Promise<EventHomePage> => {
    return request(`/event-homepage/${homePageId}/`, { method: 'PATCH', token, body: data })
  },

  deleteEventHomePage: (token: string, homePageId: number): Promise<void> => {
    return request(`/event-homepage/${homePageId}/`, { method: 'DELETE', token })
  },

  // ============================================
  // Event Navigation APIs
  // ============================================

  // Per-event vendor categories (for the "Vendors" function's category filter)
  getEventCategories: (token: string, eventId: number): Promise<PaginatedResponse<EventCategory>> => {
    return request('/event-category/', { token, params: { event: eventId } })
  },

  createEventCategory: (token: string, data: { event: number; name: string; sort?: EventCategory['sort']; order?: number }): Promise<EventCategory> => {
    return request('/event-category/', { method: 'POST', token, body: data })
  },

  updateEventCategory: (token: string, categoryId: number, data: Partial<{ name: string; sort: EventCategory['sort']; order: number }>): Promise<EventCategory> => {
    return request(`/event-category/${categoryId}/`, { method: 'PATCH', token, body: data })
  },

  deleteEventCategory: (token: string, categoryId: number): Promise<void> => {
    return request(`/event-category/${categoryId}/`, { method: 'DELETE', token })
  },

  getEventNavigations: (token: string, eventId: number, params?: {
    is_published?: boolean
    search?: string
    ordering?: string
    expand?: string
  }): Promise<PaginatedResponse<EventNavigation>> => {
    const queryParams: ParamsObject = {
      event: eventId,
    }
    if (params?.is_published !== undefined) queryParams.is_published = params.is_published
    if (params?.search) queryParams.search = params.search
    if (params?.ordering) queryParams.ordering = params.ordering
    if (params?.expand) queryParams.expand = params.expand
    return request('/event-navigation/', { token, params: queryParams })
  },

  getEventNavigation: (token: string, navigationId: number, params?: { expand?: string }): Promise<EventNavigation> => {
    const queryParams: ParamsObject = {}
    if (params?.expand) queryParams.expand = params.expand
    return request(`/event-navigation/${navigationId}/`, { token, params: queryParams })
  },

  createEventNavigation: (token: string, data: {
    event: number
    title: string
    link_type: NavigationLinkType
    order?: number
    content_page?: number | null
    external_url?: string | null
    eventpay_function?: EventPayFunction | null
    event_category?: number | null
    background_image?: string | null
    background_color?: string | null
    text_color?: string | null
    hide_title?: boolean
    is_published?: boolean
    publish_start_date?: string | null
    publish_end_date?: string | null
  }): Promise<EventNavigation> => {
    return request('/event-navigation/', { method: 'POST', token, body: data })
  },

  updateEventNavigation: (token: string, navigationId: number, data: Partial<{
    event: number
    title: string
    link_type: NavigationLinkType
    order: number
    content_page: number | null
    external_url: string | null
    eventpay_function: EventPayFunction | null
    event_category: number | null
    background_image: string | null
    background_color: string | null
    text_color: string | null
    hide_title: boolean
    is_published: boolean
    publish_start_date: string | null
    publish_end_date: string | null
  }>): Promise<EventNavigation> => {
    const { background_image, ...rest } = data
    const body = typeof background_image === 'string' ? rest : data
    return request(`/event-navigation/${navigationId}/`, { method: 'PATCH', token, body })
  },

  deleteEventNavigation: (token: string, navigationId: number): Promise<void> => {
    return request(`/event-navigation/${navigationId}/`, { method: 'DELETE', token })
  },

  // Upload navigation background image
  uploadNavigationImage: async (token: string, navigationId: number, eventId: number, file: File): Promise<EventNavigation> => {
    const formData = new FormData()
    formData.append('event', String(eventId))
    formData.append('background_image', file)
    
    const response = await fetch(`${API_BASE_URL}/event-navigation/${navigationId}/`, {
      method: 'PATCH',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Token ${token}`,
      },
      body: formData,
    })
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }))
      throw new Error(error.detail || error.message || 'Upload failed')
    }
    
    return response.json()
  },

  // ============================================
  // Event Home Page Navigation (Grid Layout) APIs
  // ============================================

  getEventHomePageNavigations: (token: string, homePageId: number): Promise<PaginatedResponse<EventHomePageNavigation>> => {
    return request('/event-homepage-navigation/', { 
      token, 
      params: { home_page: homePageId } as ParamsObject 
    })
  },

  createEventHomePageNavigation: (token: string, data: {
    home_page: number
    navigation: number
    position_x?: number
    position_y?: number
    col_span?: number
    row_span?: number
  }): Promise<EventHomePageNavigation> => {
    return request('/event-homepage-navigation/', { method: 'POST', token, body: data })
  },

  updateEventHomePageNavigation: (token: string, id: number, data: Partial<{
    position_x: number
    position_y: number
    col_span: number
    row_span: number
  }>): Promise<EventHomePageNavigation> => {
    return request(`/event-homepage-navigation/${id}/`, { method: 'PATCH', token, body: data })
  },

  deleteEventHomePageNavigation: (token: string, id: number): Promise<void> => {
    return request(`/event-homepage-navigation/${id}/`, { method: 'DELETE', token })
  },

  // Bulk update positions (for drag-and-drop reordering)
  bulkUpdateHomePageNavigationPositions: async (token: string, updates: Array<{
    id: number
    position_x: number
    position_y: number
    col_span?: number
    row_span?: number
  }>): Promise<EventHomePageNavigation[]> => {
    // Execute all updates in parallel
    const results = await Promise.all(
      updates.map(update => 
        request<EventHomePageNavigation>(`/event-homepage-navigation/${update.id}/`, {
          method: 'PATCH',
          token,
          body: {
            position_x: update.position_x,
            position_y: update.position_y,
            ...(update.col_span !== undefined && { col_span: update.col_span }),
            ...(update.row_span !== undefined && { row_span: update.row_span }),
          },
        })
      )
    )
    return results
  },

  // Export Sales (Locations view)
  // XLSX: GET /event/{id}/export/sales/
  // PDF:  GET /event/{id}/export/sales/?export_format=pdf
  exportSales: async (token: string, eventId: number, format: 'xlsx' | 'pdf'): Promise<void> => {
    const url = new URL(`${API_BASE_URL}/event/${eventId}/export/sales/`)
    if (format === 'pdf') url.searchParams.append('export_format', 'pdf')

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: { 'Authorization': `Token ${token}` },
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Export failed' }))
      throw new Error(error.detail || error.message || 'Export failed')
    }

    const contentDisposition = response.headers.get('Content-Disposition')
    let filename = `sales_event_${eventId}_${new Date().toISOString().slice(0, 10)}.${format}`
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/)
      if (match) filename = match[1]
    }

    const blob = await response.blob()
    const downloadUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = downloadUrl
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(downloadUrl)
  },

  // Export Insights (Reporting view) - GET /event/{id}/export/insights/ (PDF only)
  exportInsights: async (token: string, eventId: number): Promise<void> => {
    const url = `${API_BASE_URL}/event/${eventId}/export/insights/`

    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Authorization': `Token ${token}` },
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Export failed' }))
      throw new Error(error.detail || error.message || 'Export failed')
    }

    const contentDisposition = response.headers.get('Content-Disposition')
    let filename = `insights_event_${eventId}_${new Date().toISOString().slice(0, 10)}.pdf`
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/)
      if (match) filename = match[1]
    }

    const blob = await response.blob()
    const downloadUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = downloadUrl
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(downloadUrl)
  },

  // Export Financials
  // XLSX: GET /event/{id}/export/financials/
  // PDF:  GET /event/{id}/export/financials/?export_format=pdf
  exportFinancials: async (token: string, eventId: number, format: 'xlsx' | 'pdf'): Promise<void> => {
    const url = new URL(`${API_BASE_URL}/event/${eventId}/export/financials/`)
    if (format === 'pdf') url.searchParams.append('export_format', 'pdf')

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: { 'Authorization': `Token ${token}` },
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Export failed' }))
      throw new Error(error.detail || error.message || 'Export failed')
    }

    const contentDisposition = response.headers.get('Content-Disposition')
    let filename = `financials_event_${eventId}_${new Date().toISOString().slice(0, 10)}.${format}`
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/)
      if (match) filename = match[1]
    }

    const blob = await response.blob()
    const downloadUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = downloadUrl
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(downloadUrl)
  },

  // Site Map
  getMapConfig: (token: string, eventId: number): Promise<MapConfig> => {
    return request(`/event/${eventId}/map-config/`, { token })
  },

  updateMapConfig: async (token: string, eventId: number, data: Partial<MapConfig> | FormData): Promise<MapConfig> => {
    if (data instanceof FormData) {
      const response = await fetch(`${API_BASE_URL}/event/${eventId}/map-config/`, {
        method: 'PATCH',
        headers: { 'Authorization': `Token ${token}` },
        body: data,
      })
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to update map config' }))
        throw new Error(error.detail || error.message || 'Failed to update map config')
      }
      return response.json()
    }
    return request(`/event/${eventId}/map-config/`, { method: 'PATCH', token, body: data as Record<string, unknown> })
  },

  getMapHeatmap: (token: string, eventId: number, params?: {
    start_date?: string
    end_date?: string
    metric?: 'transactions' | 'revenue'
  }): Promise<HeatmapResponse> => {
    return request(`/event/${eventId}/map-heatmap/`, { token, params: params as ParamsObject })
  },

  getMapTimeline: (token: string, eventId: number, params?: {
    start_date?: string
    end_date?: string
    interval?: 'minute' | 'hour'
    metric?: 'transactions' | 'revenue'
    user_id?: number
    user_ids?: string
    item_id?: number
  }): Promise<TimelineResponse | JourneyResponse> => {
    return request(`/event/${eventId}/map-timeline/`, { token, params: params as ParamsObject })
  },

  getLiveStats: (
    token: string,
    eventId: number,
    params?: LiveStatsParams
  ): Promise<LiveStatsResponse> => {
    return request(`/event/${eventId}/live-stats/`, {
      token,
      params: params as ParamsObject,
    })
  },

  // Vendor Credentials
  generateVendorCredentials: async (token: string, eventId: number, params?: {
    vendor_event_ids?: number[]
    only_missing?: boolean
  }): Promise<{
    created: number
    skipped_existing: number
    failed: number
    failed_ids: number[]
    total_requested: number
  }> => {
    return request(`/event/${eventId}/vendor-credentials/generate/`, {
      method: 'POST',
      token,
      body: {
        vendor_event_ids: params?.vendor_event_ids,
        only_missing: params?.only_missing ?? true,
      },
    })
  },

  getVendorCredentials: (token: string, eventId: number, params?: {
    vendor_event_ids?: number[]
  }): Promise<Array<{
    id: number
    vendor_event: number
    username: string
    vendor_name: string
    booth: string
    event_name: string
    created: string
  }>> => {
    const queryParams: ParamsObject = {}
    if (params?.vendor_event_ids?.length) {
      queryParams.vendor_event_ids = params.vendor_event_ids.join(',')
    }
    return request(`/event/${eventId}/vendor-credentials/`, { token, params: queryParams })
  },

  exportVendorCredentialsPdf: async (token: string, eventId: number, vendorEventIds?: number[]): Promise<void> => {
    const url = new URL(`${API_BASE_URL}/event/${eventId}/export/vendor-credentials/`)
    if (vendorEventIds?.length) {
      url.searchParams.append('vendor_event_ids', vendorEventIds.join(','))
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: { 'Authorization': `Token ${token}` },
    })

    if (response.status === 409) {
      const data = await response.json()
      throw new Error(JSON.stringify({
        type: 'missing_credentials',
        missing_vendor_event_ids: data.missing_vendor_event_ids,
        missing_count: data.missing_count,
        total_requested: data.total_requested,
      }))
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Export failed' }))
      throw new Error(error.detail || error.message || 'Export failed')
    }

    const contentDisposition = response.headers.get('Content-Disposition')
    let filename = `vendor_credentials_event_${eventId}_${new Date().toISOString().slice(0, 10)}.pdf`
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/)
      if (match) filename = match[1]
    }

    const blob = await response.blob()
    const downloadUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = downloadUrl
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(downloadUrl)
  },

  // Bulk Import Vendors - uploads Excel file with vendors, menu items, and admins
  bulkImportVendors: async (token: string, eventId: number, file: File): Promise<{
    success: boolean
    message?: string
    results?: {
      vendors_created: number
      vendors_updated: number
      vendor_events_created: number
      items_created: number
      items_updated: number
      vendor_admins_created: number
      vendor_event_admins_created: number
      users_created: number
      warnings: string[]
    }
    error?: string
    errors?: string[]
  }> => {
    const formData = new FormData()
    formData.append('file', file)
    
    const response = await fetch(`${API_BASE_URL}/event/${eventId}/import-vendors/`, {
      method: 'POST',
      headers: {
        'Authorization': `Token ${token}`,
      },
      body: formData,
    })
    
    const data = await response.json().catch(() => ({}))
    
    if (!response.ok) {
      return {
        success: false,
        error: data.detail || data.error || 'Import failed',
        errors: data.errors || [],
      }
    }
    
    return {
      success: true,
      message: data.message || 'Import completed successfully',
      results: data.results || data,
    }
  },

  // Review endpoints

  getOrderReviews: (token: string, params?: ParamsObject): Promise<PaginatedResponse<OrderReview>> => {
    return request('/dashboard-order-review/', { token, params: params || {} })
  },

  getOrderReview: (token: string, id: number): Promise<OrderReview> => {
    return request(`/dashboard-order-review/${id}/`, { token })
  },

  getOrderReviewSummary: (token: string, params?: ParamsObject): Promise<OrderReviewSummary> => {
    return request('/dashboard-order-review/summary/', { token, params: params || {} })
  },

  getItemReviews: (token: string, params?: ParamsObject): Promise<PaginatedResponse<ItemReview>> => {
    return request('/dashboard-review/', { token, params: params || {} })
  },

  getItemReview: (token: string, id: number): Promise<ItemReview> => {
    return request(`/dashboard-review/${id}/`, { token })
  },

  getItemReviewSummary: (token: string, params?: ParamsObject): Promise<ItemReviewSummary> => {
    return request('/dashboard-review/summary/', { token, params: params || {} })
  },

  // Support chat endpoints

  createSupportChatSession: async (token: string, eventId: number): Promise<ChatSession> => {
    const response = await fetch(`${API_BASE_URL}/support-chat/sessions/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Token ${token}`,
      },
      body: JSON.stringify({ event_id: eventId }),
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to create support chat session' }))
      throw new Error(error.detail || `HTTP ${response.status}`)
    }
    return response.json()
  },

  /**
   * List the current user's past chat sessions for an event. Used by the
   * drawer history panel. Backend filters out empty placeholder sessions
   * and only returns sessions where staff_user === current user.
   */
  listSupportChatSessions: async (token: string, eventId: number): Promise<ChatSessionSummary[]> => {
    const response = await fetch(`${API_BASE_URL}/support-chat/sessions/?event_id=${eventId}`, {
      headers: {
        'Accept': 'application/json',
        'Authorization': `Token ${token}`,
      },
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to list chat sessions' }))
      throw new Error(error.detail || `HTTP ${response.status}`)
    }
    return response.json()
  },

  getSupportChatSession: async (token: string, sessionId: number): Promise<ChatSession> => {
    const response = await fetch(`${API_BASE_URL}/support-chat/sessions/${sessionId}/`, {
      headers: {
        'Accept': 'application/json',
        'Authorization': `Token ${token}`,
      },
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    return response.json()
  },

  endSupportChatSession: async (
    token: string,
    sessionId: number,
    resolutionStatus?: ChatResolutionStatus,
  ): Promise<ChatSession> => {
    const response = await fetch(`${API_BASE_URL}/support-chat/sessions/${sessionId}/end/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Token ${token}`,
      },
      body: JSON.stringify(resolutionStatus ? { resolution_status: resolutionStatus } : {}),
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    return response.json()
  },

  confirmPendingAction: async (
    token: string,
    sessionId: number,
    pendingActionId: number,
  ): Promise<{ pending_action: PendingAction; result: ToolResult }> => {
    const response = await fetch(`${API_BASE_URL}/support-chat/sessions/${sessionId}/confirm/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Token ${token}`,
      },
      body: JSON.stringify({ pending_action_id: pendingActionId }),
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Confirm failed' }))
      throw new Error(error.detail || `HTTP ${response.status}`)
    }
    return response.json()
  },

  getOpsOverview: async (token: string, window: string): Promise<OpsOverview> => {
    const response = await fetch(`${API_BASE_URL}/ops/overview/?window=${encodeURIComponent(window)}`, {
      headers: {
        Accept: 'application/json',
        Authorization: `Token ${token}`,
      },
      cache: 'no-store',
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Ops overview fetch failed' }))
      throw new Error(error.detail || `HTTP ${response.status}`)
    }
    return response.json()
  },

  /** Pass `window` for a rolling range, or `start`+`end` (ISO) for a fixed
   *  historical one. The two are mutually exclusive server-side. */
  getOpsSystem: async (
    token: string,
    range: { window?: string; start?: string; end?: string },
  ): Promise<OpsSystem> => {
    const params = new URLSearchParams()
    if (range.start && range.end) {
      params.set('start', range.start)
      params.set('end', range.end)
    } else {
      params.set('window', range.window ?? '6h')
    }
    const response = await fetch(`${API_BASE_URL}/ops/system/?${params.toString()}`, {
      headers: {
        Accept: 'application/json',
        Authorization: `Token ${token}`,
      },
      cache: 'no-store',
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'System metrics fetch failed' }))
      throw new Error(error.detail || `HTTP ${response.status}`)
    }
    return response.json()
  },

  getOpsEventTotals: async (token: string, start: string, end: string): Promise<OpsEventTotals> => {
    // Omit blank bounds (a cleared filter) so the backend applies its own
    // default window instead of receiving start=&end= empty params.
    const search = new URLSearchParams()
    if (start) search.set('start', start)
    if (end) search.set('end', end)
    const response = await fetch(`${API_BASE_URL}/ops/event-totals/?${search}`, {
      headers: {
        Accept: 'application/json',
        Authorization: `Token ${token}`,
      },
      cache: 'no-store',
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Event totals fetch failed' }))
      throw new Error(error.detail || `HTTP ${response.status}`)
    }
    return response.json()
  },

  getSupportChatAnalytics: async (
    token: string,
    params: SupportChatAnalyticsParams = {},
  ): Promise<SupportChatAnalytics> => {
    const search = new URLSearchParams()
    if (params.start_date) search.set('start_date', params.start_date)
    if (params.end_date) search.set('end_date', params.end_date)
    if (params.event_id != null) search.set('event_id', String(params.event_id))
    if (params.org_id != null) search.set('org_id', String(params.org_id))
    const qs = search.toString()
    const url = `${API_BASE_URL}/support-chat/analytics/${qs ? `?${qs}` : ''}`
    const response = await fetch(url, {
      headers: {
        Accept: 'application/json',
        Authorization: `Token ${token}`,
      },
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Analytics fetch failed' }))
      throw new Error(error.detail || `HTTP ${response.status}`)
    }
    return response.json()
  },

  cancelPendingAction: async (
    token: string,
    sessionId: number,
    pendingActionId: number,
  ): Promise<{ pending_action: PendingAction; result: ToolResult }> => {
    const response = await fetch(`${API_BASE_URL}/support-chat/sessions/${sessionId}/cancel/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Token ${token}`,
      },
      body: JSON.stringify({ pending_action_id: pendingActionId }),
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Cancel failed' }))
      throw new Error(error.detail || `HTTP ${response.status}`)
    }
    return response.json()
  },

  /**
   * Stream the assistant's reply as the backend produces it.
   * Yields ChatStreamEvent objects parsed from line-delimited JSON.
   */
  async *streamSupportChatMessage(
    token: string,
    sessionId: number,
    content: string,
    signal?: AbortSignal,
  ): AsyncGenerator<ChatStreamEvent> {
    const response = await fetch(`${API_BASE_URL}/support-chat/sessions/${sessionId}/messages/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/x-ndjson',
        'Authorization': `Token ${token}`,
      },
      body: JSON.stringify({ content }),
      signal,
    })

    if (!response.ok || !response.body) {
      // Surface the server-supplied reason (rate limit hit, session cap,
      // session ended, etc.) instead of a generic HTTP code so the drawer
      // can show something meaningful.
      let detail = ''
      try {
        const ct = response.headers.get('content-type') || ''
        if (ct.includes('application/json')) {
          const body = await response.json()
          detail = body?.detail || body?.message || ''
        } else {
          detail = (await response.text()).trim().slice(0, 200)
        }
      } catch {
        // ignore parse failures — fall back to status code
      }
      if (response.status === 429 && !detail) {
        detail = 'Slow down — you\'re sending messages too fast. Try again in a moment.'
      }
      throw new Error(detail || `Support chat stream failed: HTTP ${response.status}`)
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''
        for (const line of lines) {
          const trimmed = line.trim()
          if (!trimmed) continue
          yield JSON.parse(trimmed) as ChatStreamEvent
        }
      }
      const tail = buffer.trim()
      if (tail) yield JSON.parse(tail) as ChatStreamEvent
    } finally {
      reader.releaseLock()
    }
  },
}
